import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Box } from '@chakra-ui/react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import '@inovua/reactdatagrid-enterprise/index.css';
import ProductManager from 'views/ProductManager';
import Home from 'views/Home';
import OrderManager from 'views/OrderManager';
import ForgotPassword from 'views/ForgotPassword';
import RecoverPassword from 'views/RecoverPassword';
import PageNotFound from 'views/PageNotFound';
import Analytics from 'views/Analytics';
import Product from 'views/Product';
import NewProduct from 'views/NewProduct';
import EditParameters from 'views/EditParameters';
import { ConfirmProvider, UserProvider, SessionProvider, ThemeProvider } from 'contexts';
import { useAuthUser } from 'hooks';
import AppLoader from 'components/common/AppLoader/AppLoader';
import Unsubscribe from 'views/Unsubscribe';
import Settings from 'views/Settings';
import { Confirm } from 'components/common/Confirm';
import AppRoutes from 'constants/routes';

const App = () => {
  const [getAuthUser, { loading, data, error }] = useAuthUser();

  const handleLoadUser = async () => {
    await getAuthUser();
  };

  useEffect(() => {
    handleLoadUser();
  }, []);

  const renderRouter = loading ? (
    <AppLoader />
  ) : (
    <HelmetProvider>
      <SessionProvider error={error}>
        <UserProvider user={data}>
          <ConfirmProvider>
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY ?? ''}
              scriptProps={{ async: true, defer: true, appendTo: 'body' }}
              useRecaptchaNet
            >
              <Helmet defaultTitle="Variant" titleTemplate="%s | Variant" />
              <Confirm />
              <BrowserRouter>
                <Routes>
                  <Route path={AppRoutes.Analytics} element={<Analytics />} />
                  <Route path={AppRoutes.Landing} element={<Home />} />
                  <Route path={AppRoutes.OrderManager} element={<OrderManager />} />
                  <Route path={AppRoutes.EditParameters} element={<EditParameters />} />
                  <Route path={AppRoutes.Product} element={<Product />} />
                  <Route path={AppRoutes.ProductManager} element={<ProductManager />} />
                  <Route path={AppRoutes.Settings} element={<Settings />} />
                  <Route path={AppRoutes.SettingsSection} element={<Settings />} />
                  <Route path={AppRoutes.NewProduct} element={<NewProduct />} />
                  <Route path={AppRoutes.ForgotPassword} element={<ForgotPassword />} />
                  <Route path={AppRoutes.RecoverPassword} element={<RecoverPassword />} />
                  <Route path={AppRoutes.Unsubscribe} element={<Unsubscribe />} />
                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </BrowserRouter>
            </GoogleReCaptchaProvider>
          </ConfirmProvider>
        </UserProvider>
      </SessionProvider>
    </HelmetProvider>
  );

  return (
    <ThemeProvider>
      <Box className="App">{renderRouter}</Box>
    </ThemeProvider>
  );
};

export default App;
